"use client";
import React, { useState } from "react";
import { FaArrowRight } from "react-icons/fa";
import { FaqsType, SubpageContentResponse } from "./entitites/KeywordsResponse";
import { HTMLService } from "./services/html";

interface CasinoSubpageProps {
  subpage: SubpageContentResponse;
}

const subpageURL = new URL("@/app/assets/subpage.png", import.meta.url).href;

const CasinoSubpage: React.FC<CasinoSubpageProps> = ({ subpage }) => {
  // Helper to clean and split content into paragraphs
  const parseParagraphs = (html?: string) =>
    (html || "")
      .split("</p>")
      .map((p) => p.replace("<p>", "").trim())
      .filter(Boolean);

  // Helper to prepare columns and optional centered last paragraph
  const prepareColumns = (paragraphs: string[]) => {
    const result = {
      left: [] as string[],
      right: [] as string[],
      centerLast: null as string | null,
    };

    if (paragraphs.length === 0) return result;

    // If odd, remove last paragraph to render centered later
    const isOdd = paragraphs.length % 2 === 1;
    const work = [...paragraphs];
    if (isOdd) {
      result.centerLast = work.pop() || null;
    }

    const mid = Math.ceil(work.length / 2);
    result.left = work.slice(0, mid);
    result.right = work.slice(mid);
    return result;
  };

  // About Section
  const aboutParagraphs = parseParagraphs(subpage?.aboutSection?.content);
  const aboutData = prepareColumns(aboutParagraphs);

  // Unique Section
  const uniqueParagraphs = parseParagraphs(subpage?.uniqueSection?.content);
  const uniqueData = prepareColumns(uniqueParagraphs);

  // FAQs
  const faqs: FaqsType[] = subpage?.faqSection?.faqs || [];
  const [activeAccordion, setActiveAccordion] = useState<number | null>(null);

  // Small paragraph renderer to keep code DRY
  const Paragraph = ({
    text,
    centered = false,
  }: {
    text: string;
    centered?: boolean;
  }) => (
    <div
      className={`text-gray-300 text-base md:text-lg leading-loose tracking-wide first-letter:text-4xl first-letter:font-bold first-letter:text-orange-400 first-letter:mr-2 first-letter:float-left ${
        centered
          ? "md:col-span-2 text-center max-w-2xl mx-auto first-letter:float-none first-letter:mr-0"
          : "text-justify"
      }`}
    >
      {HTMLService.toReact(text)}
    </div>
  );

  return (
    <div className="min-h-screen text-white pt-20">
      {/* Hero Section */}
      <section
        id="hero"
        className="relative flex items-center justify-center overflow-hidden bg-slate-950 text-white py-32"
        style={{
          backgroundImage: `url(${subpageURL})`,
          backgroundSize: "cover",
          backgroundPosition: "center",
        }}
      >
        <div className="absolute inset-0 bg-black/30" />
        <div className="relative z-10 container mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <span className="inline-block mb-6 px-5 py-2 rounded-full text-sm font-bold uppercase tracking-wider text-yellow-300 border border-yellow-500/30 bg-gradient-to-r from-yellow-500/20 to-purple-500/20 backdrop-blur-sm">
            Details
          </span>
          <h1 className="text-4xl sm:text-5xl md:text-6xl font-black mb-6 leading-tight drop-shadow-2xl">
            {subpage?.pageHeader?.title || subpage?.h1 || "Casino Details"}
          </h1>
          {subpage?.pageHeader?.subtitle && (
            <p className="text-xl text-gray-300 max-w-3xl mx-auto font-medium leading-relaxed">
              {subpage.pageHeader.subtitle}
            </p>
          )}
        </div>
      </section>

      {/* About Section */}
      <section className="relative bg-gradient-to-b from-slate-950 via-slate-900 to-slate-950 py-28 overflow-hidden flex justify-center">
        <div className="absolute inset-0 bg-black/25" />
        <div className="relative z-10 max-w-7xl w-full px-6 sm:px-8 lg:px-12">
          <div className="text-center mb-14">
            <h2 className="text-4xl md:text-5xl font-extrabold relative inline-block">
              <span>{subpage?.aboutSection?.title || "About Us"}</span>
              <span className="absolute -bottom-2 left-1/2 -translate-x-1/2 w-20 h-1 bg-blue-500 rounded-full" />
            </h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-14 justify-center">
            {/* Left column */}
            <div className="space-y-8 md:pr-8 border-b md:border-b-0 md:border-r border-gray-700 pb-8 md:pb-0 text-center md:text-left">
              {aboutData.left.length > 0 ? (
                aboutData.left.map((p, idx) => <Paragraph key={idx} text={p} />)
              ) : (
                <p className="text-gray-400 italic">
                  Our story is coming soon. Stay tuned for more details.
                </p>
              )}
            </div>

            {/* Right column */}
            <div className="space-y-8 md:pl-8 text-center md:text-left">
              {aboutData.right.length > 0 ? (
                aboutData.right.map((p, idx) => (
                  <Paragraph key={idx} text={p} />
                ))
              ) : (
                <p className="text-gray-400 italic">
                  Our story is coming soon. Stay tuned for more details.
                </p>
              )}
            </div>

            {/* Centered last paragraph (if any) */}
            {aboutData.centerLast && (
              <div className="md:col-span-2 mt-4">
                <Paragraph text={aboutData.centerLast} centered />
              </div>
            )}
          </div>
        </div>
      </section>

      {/* Unique Section */}
      <section className="relative bg-[#131C42] py-28 overflow-hidden flex justify-center">
        <div className="absolute inset-0 bg-black/25" />
        <div className="relative z-10 max-w-7xl w-full px-6 sm:px-8 lg:px-12">
          <div className="text-center mb-14">
            <h2 className="text-4xl md:text-5xl font-extrabold relative inline-block">
              <span>
                {subpage?.uniqueSection?.title || "What Makes Us Unique"}
              </span>
              <span className="absolute -bottom-2 left-1/2 -translate-x-1/2 w-20 h-1 bg-blue-500 rounded-full" />
            </h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-14 justify-center">
            {/* Left column */}
            <div className="space-y-8 md:pr-8 border-b md:border-b-0 md:border-r border-gray-700 pb-8 md:pb-0 text-center md:text-left">
              {uniqueData.left.length > 0 ? (
                uniqueData.left.map((p, idx) => (
                  <Paragraph key={idx} text={p} />
                ))
              ) : (
                <p className="text-gray-400 italic">
                  Discover how we stand apart from the rest.
                </p>
              )}
            </div>

            {/* Right column */}
            <div className="space-y-8 md:pl-8 text-center md:text-left">
              {uniqueData.right.length > 0 ? (
                uniqueData.right.map((p, idx) => (
                  <Paragraph key={idx} text={p} />
                ))
              ) : (
                <p className="text-gray-400 italic">
                  Discover how we stand apart from the rest.
                </p>
              )}
            </div>

            {/* Centered last paragraph (if any) */}
            {uniqueData.centerLast && (
              <div className="md:col-span-2 mt-4">
                <Paragraph text={uniqueData.centerLast} centered />
              </div>
            )}
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      {faqs.length > 0 && (
        <section className="relative bg-gradient-to-b from-slate-950 via-slate-900 to-slate-950 py-20 overflow-hidden flex justify-center">
          <div className="relative z-10 max-w-5xl w-full px-6 sm:px-8 lg:px-12 space-y-8">
            <div className="text-center mb-12">
              <h2 className="text-4xl md:text-5xl font-extrabold relative inline-block">
                <span>FAQs</span>
                <span className="absolute -bottom-2 left-1/2 -translate-x-1/2 w-20 h-1 bg-cyan-500 rounded-full" />
              </h2>
              <p className="mt-4 text-gray-400 text-base md:text-lg max-w-2xl mx-auto leading-relaxed">
                Everything you need to know about HypeGrow, explained clearly
                and concisely.
              </p>
            </div>

            <div className="grid gap-6">
              {faqs.map((faq, index) => (
                <div key={index} className="group relative">
                  <div className="relative bg-slate-800/40 backdrop-blur-xl rounded-2xl border border-cyan-500/20 overflow-hidden transition-all">
                    <button
                      onClick={() =>
                        setActiveAccordion(
                          activeAccordion === index ? null : index,
                        )
                      }
                      className="w-full text-left p-6 md:p-7 flex items-start justify-between gap-4 hover:bg-cyan-500/5 transition-colors"
                    >
                      <span className="text-lg md:text-xl font-semibold text-white flex-1">
                        {faq.question || "Untitled Question"}
                      </span>
                      <div
                        className={`mt-1 p-2 transition-all flex-shrink-0 ${
                          activeAccordion === index ? "rotate-90" : ""
                        }`}
                      >
                        <FaArrowRight className="text-cyan-400 w-4 h-4 md:w-5 md:h-5" />
                      </div>
                    </button>

                    <div
                      className={`transition-max-height duration-300 ease-in-out overflow-hidden ${
                        activeAccordion === index ? "max-h-96" : "max-h-0"
                      }`}
                    >
                      <div className="px-6 md:px-7 pb-6">
                        <div className="border-t border-cyan-500/20 pt-5">
                          <p className="text-gray-300 text-base md:text-lg leading-relaxed">
                            {faq.answer ||
                              "No answer available for this question yet."}
                          </p>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>
      )}
    </div>
  );
};

export default CasinoSubpage;
